import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../utils/api';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Users, MessageSquare, Shield, Trash2, Ban, ArrowLeft,
  BarChart3, Radio, Video, UserCheck, UserX, Activity,
  ChevronRight, Search, RefreshCw, XCircle, Crown,
} from 'lucide-react';

// ── Mini Bar Chart ──
const MiniChart = ({ data, color = '#4338CA', label }) => {
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div data-testid={`chart-${label}`}>
      <p className="text-xs text-[#94A3B8] mb-2 font-medium">{label}</p>
      <div className="flex items-end gap-1 h-20">
        {data.map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div
              className="w-full rounded-t-sm transition-all"
              style={{ height: `${Math.max((d.count / max) * 100, 4)}%`, backgroundColor: color, minHeight: '3px' }}
              title={`${d.date}: ${d.count}`}
            />
            <span className="text-[9px] text-[#94A3B8] whitespace-nowrap">{d.date.split(' ')[1]}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Stat Card ──
const StatCard = ({ icon: Icon, label, value, color, subtext }) => (
  <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 flex items-start gap-3" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0`} style={{ backgroundColor: `${color}15` }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <div>
      <p className="text-2xl font-bold text-[#0F172A]">{value ?? '—'}</p>
      <p className="text-xs text-[#94A3B8]">{label}</p>
      {subtext && <p className="text-[10px] text-[#64748B] mt-0.5">{subtext}</p>}
    </div>
  </div>
);

const AdminDashboard = ({ onBack }) => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [live, setLive] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    try {
      const res = await adminAPI.getStats();
      setStats(res.data);
    } catch (e) {}
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const res = await adminAPI.getUsers();
      setUsers(res.data);
    } catch (e) {}
  }, []);

  const loadMeetings = useCallback(async () => {
    try {
      const res = await adminAPI.getMeetings();
      setMeetings(res.data);
    } catch (e) {}
  }, []);

  const loadLive = useCallback(async () => {
    try {
      const res = await adminAPI.getLive();
      setLive(res.data);
    } catch (e) {}
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadStats(), loadUsers(), loadMeetings(), loadLive()]);
      setLoading(false);
    };
    init();
  }, []);

  // Auto-refresh live monitor
  useEffect(() => {
    if (activeTab !== 'live') return;
    const interval = setInterval(loadLive, 5000);
    return () => clearInterval(interval);
  }, [activeTab, loadLive]);

  const handleBan = async (userId) => {
    try {
      const res = await adminAPI.banUser(userId);
      toast.success(res.data.message);
      loadUsers();
    } catch (e) { toast.error('Failed'); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user and all their data?')) return;
    try {
      await adminAPI.deleteUser(userId);
      toast.success('User deleted');
      loadUsers(); loadStats();
    } catch (e) { toast.error('Failed'); }
  };

  const handleForceEnd = async (meetingId) => {
    try {
      await adminAPI.forceEndMeeting(meetingId);
      toast.success('Meeting force-ended');
      loadMeetings(); loadLive(); loadStats();
    } catch (e) { toast.error('Failed'); }
  };

  const refreshAll = () => { loadStats(); loadUsers(); loadMeetings(); loadLive(); toast.success('Refreshed'); };

  const filteredUsers = users.filter(u =>
    !searchQuery || u.name?.toLowerCase().includes(searchQuery.toLowerCase()) || u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'meetings', label: 'Meetings', icon: Video },
    { id: 'live', label: 'Live Monitor', icon: Radio },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-[#F8FAFC] flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <div className="w-56 bg-[#0F172A] flex flex-col shrink-0">
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#4338CA]" />
            <span className="text-white font-bold text-sm">Admin Panel</span>
          </div>
        </div>
        <nav className="flex-1 p-2 space-y-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id ? 'bg-[#4338CA] text-white' : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.id === 'live' && live && (
                <span className="ml-auto w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              )}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button onClick={onBack} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-white/50 hover:text-white hover:bg-white/5 text-sm" data-testid="admin-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back to App
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="bg-white border-b border-[#E2E8F0] px-6 py-3 flex items-center justify-between shrink-0">
          <h1 className="text-lg font-bold text-[#0F172A]">{tabs.find(t => t.id === activeTab)?.label}</h1>
          <button onClick={refreshAll} className="flex items-center gap-1.5 text-sm text-[#64748B] hover:text-[#0F172A] transition-colors" data-testid="admin-refresh">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-6 max-w-6xl">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4338CA]" />
              </div>
            ) : (
              <>
                {/* ═══ DASHBOARD ═══ */}
                {activeTab === 'dashboard' && stats && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <StatCard icon={Users} label="Total Users" value={stats.total_users} color="#4338CA" />
                      <StatCard icon={MessageSquare} label="Messages" value={stats.total_messages} color="#059669" />
                      <StatCard icon={Video} label="Active Meetings" value={stats.active_meetings} color="#D97706" subtext={`${stats.total_meetings} total`} />
                      <StatCard icon={Activity} label="Online Now" value={stats.online_users} color="#DC2626" />
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <StatCard icon={UserCheck} label="Total Channels" value={stats.total_channels} color="#7C3AED" />
                      <StatCard icon={UserX} label="Banned Users" value={stats.banned_users} color="#EF4444" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white rounded-xl border border-[#E2E8F0] p-4">
                        <MiniChart data={stats.msgs_per_day || []} color="#059669" label="Messages (7 days)" />
                      </div>
                      <div className="bg-white rounded-xl border border-[#E2E8F0] p-4">
                        <MiniChart data={stats.meetings_per_day || []} color="#D97706" label="Meetings (7 days)" />
                      </div>
                      <div className="bg-white rounded-xl border border-[#E2E8F0] p-4">
                        <MiniChart data={stats.users_per_day || []} color="#4338CA" label="New Users (7 days)" />
                      </div>
                    </div>
                  </div>
                )}

                {/* ═══ USERS ═══ */}
                {activeTab === 'users' && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                        <input
                          type="text" placeholder="Search users..." value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 border border-[#E2E8F0] rounded-lg text-sm focus:ring-2 focus:ring-[#4338CA]/20 focus:border-[#4338CA] outline-none"
                          data-testid="admin-user-search"
                        />
                      </div>
                      <span className="text-sm text-[#94A3B8]">{filteredUsers.length} users</span>
                    </div>
                    <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                            <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">User</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">Email</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">Role</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">Status</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredUsers.map(u => (
                            <tr key={u.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]" data-testid={`admin-user-${u.id}`}>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2.5">
                                  <Avatar className="w-8 h-8"><AvatarFallback className="bg-[#4338CA] text-white text-xs">{u.name?.charAt(0).toUpperCase()}</AvatarFallback></Avatar>
                                  <span className="text-sm font-medium text-[#0F172A]">{u.name}</span>
                                  {u.role === 'admin' && <Crown className="w-3.5 h-3.5 text-amber-500" />}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-[#64748B]">{u.email}</td>
                              <td className="px-4 py-3">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.role === 'admin' ? 'bg-[#4338CA]/10 text-[#4338CA]' : 'bg-[#F1F5F9] text-[#64748B]'}`}>{u.role || 'user'}</span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.status === 'banned' ? 'bg-red-100 text-red-600' : u.status === 'online' ? 'bg-green-100 text-green-600' : 'bg-[#F1F5F9] text-[#64748B]'}`}>{u.status || 'offline'}</span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                {u.role !== 'admin' && (
                                  <div className="flex items-center justify-end gap-1">
                                    <button onClick={() => handleBan(u.id)} className={`p-1.5 rounded-lg hover:bg-[#F1F5F9] ${u.status === 'banned' ? 'text-green-500' : 'text-amber-500'}`} title={u.status === 'banned' ? 'Unban' : 'Ban'} data-testid={`ban-${u.id}`}>
                                      <Ban className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => handleDeleteUser(u.id)} className="p-1.5 rounded-lg hover:bg-[#F1F5F9] text-red-500" title="Delete" data-testid={`delete-user-${u.id}`}>
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* ═══ MEETINGS ═══ */}
                {activeTab === 'meetings' && (
                  <div className="space-y-4">
                    <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                            <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">Meeting</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">Host</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">Participants</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">Status</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">Created</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {meetings.map(m => (
                            <tr key={m.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]" data-testid={`admin-meeting-${m.id}`}>
                              <td className="px-4 py-3">
                                <div>
                                  <p className="text-sm font-medium text-[#0F172A]">{m.title}</p>
                                  <p className="text-xs text-[#94A3B8] font-mono">{m.join_code}</p>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-[#64748B]">{m.host_name || '—'}</td>
                              <td className="px-4 py-3">
                                <span className="text-sm font-medium text-[#0F172A]">{m.participants?.length || 0}</span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                  m.status === 'live' ? 'bg-green-100 text-green-600' :
                                  m.status === 'waiting' ? 'bg-amber-100 text-amber-600' :
                                  'bg-[#F1F5F9] text-[#64748B]'
                                }`}>{m.status}</span>
                              </td>
                              <td className="px-4 py-3 text-xs text-[#94A3B8]">
                                {m.created_at ? new Date(m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                              </td>
                              <td className="px-4 py-3 text-right">
                                {m.status !== 'ended' && (
                                  <button onClick={() => handleForceEnd(m.id)} className="text-xs px-3 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 font-medium" data-testid={`end-meeting-${m.id}`}>
                                    <XCircle className="w-3.5 h-3.5 inline mr-1" /> End
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                          {meetings.length === 0 && (
                            <tr><td colSpan={6} className="text-center py-8 text-[#94A3B8] text-sm">No meetings yet</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* ═══ LIVE MONITOR ═══ */}
                {activeTab === 'live' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <StatCard icon={Activity} label="Online Users" value={live?.online_users ?? 0} color="#059669" />
                      <StatCard icon={Radio} label="Active Meetings" value={live?.live_meetings?.length ?? 0} color="#DC2626" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /> Live Meetings
                        <span className="text-xs text-[#94A3B8] font-normal">(auto-refreshes every 5s)</span>
                      </h3>
                      {live?.live_meetings?.length > 0 ? (
                        <div className="space-y-2">
                          {live.live_meetings.map(m => (
                            <div key={m.id} className="bg-white rounded-xl border border-[#E2E8F0] p-4 flex items-center justify-between" data-testid={`live-meeting-${m.id}`}>
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                                  <Video className="w-5 h-5 text-red-500" />
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-[#0F172A]">{m.title}</p>
                                  <p className="text-xs text-[#94A3B8]">Host: {m.host_name || '—'} | {m.participants?.length || 0} participants | Code: {m.join_code}</p>
                                </div>
                              </div>
                              <button onClick={() => handleForceEnd(m.id)} className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors" data-testid={`force-end-${m.id}`}>
                                Force End
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="bg-white rounded-xl border border-[#E2E8F0] p-8 text-center text-[#94A3B8] text-sm">
                          No active meetings right now
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default AdminDashboard;
